// src/composables/useCourses.js
import { ref, computed } from 'vue'
import { isVisibleCourse } from '@/utils/courseUtils'

/**
 * Composable для работы с курсами
 */
export function useCourses() {
	const courses = ref([])
	const loading = ref(false)
	const searchQuery = ref('')
	const currentPage = ref(1)
	const coursesPerPage = ref(6)

	/**
	 * Фильтрованные курсы
	 */
	const filteredCourses = computed(() => {
		if (!searchQuery.value.trim()) {
			return courses.value
		}
		const query = searchQuery.value.toLowerCase()
		return courses.value.filter(
			course =>
				course.title.toLowerCase().includes(query) ||
				course.description?.toLowerCase().includes(query) ||
				course.teacher_name?.toLowerCase().includes(query)
		)
	})

	/**
	 * Общее количество страниц
	 */
	const totalPages = computed(() => {
		return Math.ceil(filteredCourses.value.length / coursesPerPage.value)
	})

	/**
	 * Курсы для текущей страницы
	 */
	const paginatedCourses = computed(() => {
		const start = (currentPage.value - 1) * coursesPerPage.value
		const end = start + coursesPerPage.value
		return filteredCourses.value.slice(start, end)
	})

	/**
	 * Загрузка всех курсов
	 */
	async function loadCourses() {
		loading.value = true
		try {
			const { coursesService } = await import('@/services/courses')
			const apiResponse = await coursesService.getAll()

			const rawCourses = Array.isArray(apiResponse)
				? apiResponse
				: apiResponse?.courses || []

			courses.value = rawCourses.filter(isVisibleCourse)

			console.log('✅ [useCourses] Загружено курсов:', courses.value.length)
		} catch (error) {
			console.error('❌ [useCourses] Ошибка загрузки:', error)
			courses.value = []
		} finally {
			loading.value = false
		}
	}

	/**
	 * Поиск курсов
	 */
	function search(query) {
		searchQuery.value = query
		currentPage.value = 1
	}

	/**
	 * Переход на страницу
	 */
	function goToPage(page) {
		currentPage.value = page
	}

	/**
	 * Предыдущая страница
	 */
	function prevPage() {
		if (currentPage.value > 1) {
			currentPage.value--
		}
	}

	/**
	 * Следующая страница
	 */
	function nextPage() {
		if (currentPage.value < totalPages.value) {
			currentPage.value++
		}
	}

	return {
		courses,
		loading,
		searchQuery,
		currentPage,
		coursesPerPage,
		filteredCourses,
		totalPages,
		paginatedCourses,
		loadCourses,
		search,
		goToPage,
		prevPage,
		nextPage,
	}
}

/**
 * Composable для курсов студента (на которые записан)
 */
export function useEnrolledCourses() {
	const enrolledCourses = ref([])
	const loading = ref(false)
	const normalizePercent = value => {
		const parsed = Number(value)
		if (!Number.isFinite(parsed)) return 0
		return Math.max(0, Math.min(100, parsed))
	}

	/**
	 * ID курсов студента
	 */
	const enrolledCourseIds = computed(() => {
		return enrolledCourses.value
			.map(c => {
				const id = c.courseId || c.course_id
				return typeof id === 'string' ? parseInt(id, 10) : id
			})
			.filter(id => id !== null && !isNaN(id))
	})

	/**
	 * Загрузка курсов студента
	 */
	async function loadEnrolledCourses() {
		loading.value = true
		try {
			const { coursesService } = await import('@/services/courses')
			const apiResponse = await coursesService.getMy()

			const list = Array.isArray(apiResponse)
				? apiResponse
				: apiResponse?.courses || apiResponse?.data || []

			enrolledCourses.value = list.map(enrollment => {
				const course = enrollment.course || enrollment

				return {
					id: enrollment.id,
					courseId: enrollment.course_id || course.id || enrollment.courseId,
					title: course.title || enrollment.title,
					coverImage:
						course.cover_image_url || enrollment.cover_image_url || '',
					enrollment_status:
						enrollment.status ??
						enrollment.enrollment_status ??
						enrollment.course_status ??
						null,
					progressPercent:
						normalizePercent(
							enrollment.progress_percent ?? enrollment.progress ?? 0
						),
					currentLesson:
						enrollment.current_lesson_number ?? enrollment.current_lesson ?? 0,
					totalLessons:
						enrollment.total_lessons ??
						enrollment.lessons_total ??
						course.lessons_count ??
						0,
				}
			})

			console.log(
				'✅ [useEnrolledCourses] Загружено:',
				enrolledCourses.value.length
			)
		} catch (error) {
			console.error('❌ [useEnrolledCourses] Ошибка:', error)
			enrolledCourses.value = []
		} finally {
			loading.value = false
		}
	}

	return {
		enrolledCourses,
		loading,
		enrolledCourseIds,
		loadEnrolledCourses,
	}
}
